import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Spinner, Modal } from "@/pages/admin/AdminCategories";

const DIFFICULTIES = ["Facile", "Moyen", "Difficile"];
const PAGE_SIZE = 10;

export default function AdminQuestions() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState({ search: "", category_id: "", tag: "", difficulty: "", active: "", sort: "created_desc" });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, ...filters };
      Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
      const { data } = await api.get("/admin/questions", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data));
    api.get("/admin/tags").then(({ data }) => setTags(data));
  }, []);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const catName = (id) => cats.find((c) => c.id === id)?.name || "?";

  const remove = async (q) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    await api.delete(`/admin/questions/${q.id}`);
    toast.success("Question supprimée");
    load();
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Questions</h1>
          <p className="text-sm text-slate-500"><span data-testid="questions-total">{data.total}</span> question(s) au total</p>
        </div>
        <button data-testid="add-question-btn" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white active:scale-95">
          <Plus className="h-4 w-4" /> Nouvelle
        </button>
      </div>

      {/* Filters */}
      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 sm:col-span-2 lg:col-span-1">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="question-search" value={filters.search} onChange={(e) => setFilter("search", e.target.value)} placeholder="Rechercher..." className="w-full py-2.5 text-sm outline-none" />
        </div>
        <select data-testid="filter-category" value={filters.category_id} onChange={(e) => setFilter("category_id", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none">
          <option value="">Toutes catégories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-tag" value={filters.tag} onChange={(e) => setFilter("tag", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none">
          <option value="">Tous tags</option>
          {tags.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
        <select data-testid="filter-difficulty" value={filters.difficulty} onChange={(e) => setFilter("difficulty", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none">
          <option value="">Toutes difficultés</option>
          {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="filter-active" value={filters.active} onChange={(e) => setFilter("active", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none">
          <option value="">Actives + inactives</option>
          <option value="true">Actives</option>
          <option value="false">Inactives</option>
        </select>
        <select data-testid="filter-sort" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none">
          <option value="created_desc">Plus récentes</option>
          <option value="created_asc">Plus anciennes</option>
          <option value="text_asc">Texte A→Z</option>
          <option value="text_desc">Texte Z→A</option>
        </select>
      </div>

      {loading ? <Spinner /> : (
        <div className="space-y-3" data-testid="admin-question-table">
          {data.items.map((q) => (
            <div key={q.id} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900">{q.text}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {q.category_ids.map((id) => <span key={id} className="rounded-full bg-dh-red/10 px-2.5 py-0.5 text-xs font-semibold text-dh-red">{catName(id)}</span>)}
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">{q.difficulty}</span>
                    {q.tags.map((t) => <span key={t} className="rounded-full bg-dh-blue/10 px-2.5 py-0.5 text-xs text-dh-blue">#{t}</span>)}
                    {!q.active && <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs text-slate-500">Inactive</span>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => setEditing(q)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(q)} className="rounded-lg p-2 text-dh-red hover:bg-dh-red/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
          {data.items.length === 0 && <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-400">Aucune question trouvée.</p>}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button data-testid="prev-page" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Précédent
          </button>
          <span className="text-sm text-slate-500">Page {page} / {totalPages}</span>
          <button data-testid="next-page" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-40">
            Suivant <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {editing && (
        <QuestionForm
          question={editing}
          cats={cats}
          allTags={tags}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function QuestionForm({ question, cats, allTags, onClose, onSaved }) {
  const isEdit = !!question.id;
  const [text, setText] = useState(question.text || "");
  const [answers, setAnswers] = useState(question.answers || ["", "", ""]);
  const [correct, setCorrect] = useState(question.correct_index ?? 0);
  const [categoryIds, setCategoryIds] = useState(question.category_ids || []);
  const [tags, setTags] = useState(question.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [difficulty, setDifficulty] = useState(question.difficulty || "Facile");
  const [themeSource, setThemeSource] = useState(question.theme_source || "");
  const [explanation, setExplanation] = useState(question.explanation || "");
  const [active, setActive] = useState(question.active ?? true);
  const [saving, setSaving] = useState(false);

  const toggleCat = (id) => setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const addTag = (t) => { const v = t.trim(); if (v && !tags.includes(v)) setTags([...tags, v]); setTagInput(""); };

  const save = async () => {
    if (!text.trim()) return toast.error("Le texte de la question est obligatoire");
    if (answers.some((a) => !a.trim())) return toast.error("Les 3 réponses sont obligatoires");
    if (categoryIds.length === 0) return toast.error("Choisissez au moins une catégorie");
    setSaving(true);
    const payload = { text: text.trim(), answers: answers.map((a) => a.trim()), correct_index: correct, category_ids: categoryIds, tags, difficulty, theme_source: themeSource.trim(), explanation: explanation.trim(), active };
    try {
      if (isEdit) await api.put(`/admin/questions/${question.id}`, payload);
      else await api.post("/admin/questions", payload);
      toast.success(isEdit ? "Question modifiée" : "Question créée");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Modifier la question" : "Nouvelle question"} onClose={onClose} wide>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        <Field label="Question">
          <textarea data-testid="qf-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-dh-red" placeholder="Texte de la question" />
        </Field>

        <Field label="Réponses (cochez la bonne)">
          <div className="space-y-2">
            {answers.map((a, i) => (
              <div key={i} className={`flex items-center gap-2 rounded-xl border px-3 ${correct === i ? "border-dh-green bg-dh-green/5" : "border-slate-200"}`}>
                <button type="button" data-testid={`qf-correct-${i}`} onClick={() => setCorrect(i)} className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${correct === i ? "border-dh-green bg-dh-green text-white" : "border-slate-300 text-transparent"}`}>
                  <Check className="h-4 w-4" />
                </button>
                <input data-testid={`qf-answer-${i}`} value={a} onChange={(e) => setAnswers((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))} placeholder={`Réponse ${i + 1}`} className="w-full py-2.5 outline-none" />
              </div>
            ))}
          </div>
        </Field>

        <Field label="Catégories">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c.id} type="button" data-testid={`qf-cat-${c.id}`} onClick={() => toggleCat(c.id)} className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${categoryIds.includes(c.id) ? "border-dh-red bg-dh-red text-white" : "border-slate-200 text-slate-600"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Tags">
          <div className="mb-2 flex flex-wrap gap-2">
            {tags.map((t) => (
              <span key={t} className="flex items-center gap-1 rounded-full bg-dh-blue/10 px-3 py-1 text-sm text-dh-blue">
                #{t} <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))}>×</button>
              </span>
            ))}
          </div>
          <input
            data-testid="qf-tag-input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
            list="all-tags"
            placeholder="Ajouter un tag et Entrée"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-dh-red"
          />
          <datalist id="all-tags">{allTags.map((t) => <option key={t.id} value={t.name} />)}</datalist>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Difficulté">
            <select data-testid="qf-difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-dh-red">
              {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <button type="button" data-testid="qf-active" onClick={() => setActive(!active)} className={`w-full rounded-xl border px-4 py-2.5 text-sm font-semibold ${active ? "border-dh-green bg-dh-green/10 text-dh-green" : "border-slate-200 text-slate-500"}`}>
              {active ? "Active" : "Inactive"}
            </button>
          </Field>
        </div>

        <Field label="Thème source (interne, facultatif)">
          <input data-testid="qf-theme" value={themeSource} onChange={(e) => setThemeSource(e.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-dh-red" placeholder="Ex. ogcnice ; années 90" />
        </Field>

        <Field label="Explication (facultatif)">
          <textarea data-testid="qf-explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-dh-red" placeholder="Courte explication affichée après la réponse" />
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-3 border-t border-slate-100 pt-4">
        <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold">Annuler</button>
        <button data-testid="save-question-btn" onClick={save} disabled={saving} className="rounded-xl bg-dh-red px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? "..." : "Enregistrer"}</button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      {children}
    </div>
  );
}
